import { TFunction } from 'i18next'
import { z } from 'zod'

import { NeighbourhoodPerk } from '@/api/neighbourhood-perks'
import { isValidLink } from '@/utils/link'

export const PERK_DISCOUNT_LENGTH = 4
export const PERK_DESCRIPTION_MAX_LENGTH = 510
export const PERK_TEXT_MAX_LENGTH = 255

export const PerkTypeSchema = z.enum(['link', 'url'])

export const PerkType = PerkTypeSchema.enum

export type PerkType = z.infer<typeof PerkTypeSchema>

export const DiscountTypeSchema = z.enum(['free', 'percent', 'amount', 'custom'])

export const DiscountType = DiscountTypeSchema.enum

export type DiscountType = z.infer<typeof DiscountTypeSchema>

export const PERCENT_DISCOUNT_MAX = 100
export const AMOUNT_DISCOUNT_MAX = 999

export const DISCOUNT_SYMBOLS = { amount: '$', percent: '%' } as const

const FREE_DISCOUNT = 'Free'
const PERCENT_DISCOUNT_REGEX = /^-?(\d{1,3})%$/
const AMOUNT_DISCOUNT_REGEX = /^\$(\d{1,3})$/
const WHOLE_NUMBER_REGEX = /^\d+$/

export const parseDiscount = (tag: string): { type: DiscountType; value: string } => {
  if (tag.toLowerCase() === FREE_DISCOUNT.toLowerCase()) {
    return { type: DiscountType.free, value: '' }
  }
  const percent = PERCENT_DISCOUNT_REGEX.exec(tag)
  if (percent) {
    return { type: DiscountType.percent, value: String(Number(percent[1])) }
  }
  const amount = AMOUNT_DISCOUNT_REGEX.exec(tag)
  if (amount) {
    return { type: DiscountType.amount, value: String(Number(amount[1])) }
  }
  return { type: tag ? DiscountType.custom : DiscountType.free, value: tag }
}

export const buildDiscount = (type: DiscountType, value: string) => {
  const trimmed = value.trim()
  switch (type) {
    case DiscountType.percent:
      return `-${Number(trimmed)}%`
    case DiscountType.amount:
      return `$${Number(trimmed)}`
    case DiscountType.custom:
      return trimmed
    default:
      return FREE_DISCOUNT
  }
}

const isWholeNumberInRange = (value: string, max: number) =>
  WHOLE_NUMBER_REGEX.test(value) && Number(value) >= 1 && Number(value) <= max

interface PerkSchemaOptions {
  hasExistingImage: boolean
  hasExistingLogo: boolean
}

export const makePerkFormSchema = (
  t: TFunction<['organisation']>,
  { hasExistingImage, hasExistingLogo }: PerkSchemaOptions,
) => {
  const textMaxLength = t('organisation:places.perks.validation.textMaxLength', {
    max: PERK_TEXT_MAX_LENGTH,
  })

  return z
    .object({
      category: z.string().min(1, t('organisation:places.perks.validation.categoryRequired')),
      description: z
        .string()
        .trim()
        .min(1, t('organisation:places.perks.validation.descriptionRequired'))
        .max(
          PERK_DESCRIPTION_MAX_LENGTH,
          t('organisation:places.perks.validation.descriptionMaxLength', {
            max: PERK_DESCRIPTION_MAX_LENGTH,
          }),
        ),
      discountType: DiscountTypeSchema,
      discountValue: z.string().trim(),
      image: z.array(z.instanceof(File)).optional(),
      link: z.string().trim().max(PERK_TEXT_MAX_LENGTH, textMaxLength).optional(),
      logo: z.array(z.instanceof(File)).optional(),
      name: z
        .string()
        .trim()
        .min(1, t('organisation:places.perks.validation.nameRequired'))
        .max(PERK_TEXT_MAX_LENGTH, textMaxLength),
      perkType: PerkTypeSchema,
      url: z.string().trim().max(PERK_TEXT_MAX_LENGTH, textMaxLength).optional(),
    })
    .superRefine((data, ctx) => {
      if (!hasExistingLogo && !data.logo?.length) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.logoRequired'),
          path: ['logo'],
        })
      }
      if (!hasExistingImage && !data.image?.length) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.imageRequired'),
          path: ['image'],
        })
      }
      if (
        data.discountType === DiscountType.percent &&
        !isWholeNumberInRange(data.discountValue, PERCENT_DISCOUNT_MAX)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.discountPercentInvalid'),
          path: ['discountValue'],
        })
      }
      if (
        data.discountType === DiscountType.amount &&
        !isWholeNumberInRange(data.discountValue, AMOUNT_DISCOUNT_MAX)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.discountAmountInvalid'),
          path: ['discountValue'],
        })
      }
      if (
        data.discountType === DiscountType.custom &&
        (!data.discountValue || data.discountValue.length > PERK_DISCOUNT_LENGTH)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.discountCustomInvalid', {
            length: PERK_DISCOUNT_LENGTH,
          }),
          path: ['discountValue'],
        })
      }
      if (data.perkType === PerkType.link && !data.link) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.promoCodeRequired'),
          path: ['link'],
        })
      }
      if (data.perkType === PerkType.url && !isValidLink(data.url ?? '')) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.perks.validation.urlInvalid'),
          path: ['url'],
        })
      }
    })
}

export type PerkFormData = z.infer<ReturnType<typeof makePerkFormSchema>>

export const getPerkFormDefaults = (perk?: NeighbourhoodPerk): PerkFormData => ({
  category: perk?.category ?? '',
  description: perk?.description ?? '',
  discountType: parseDiscount(perk?.tag ?? '').type,
  discountValue: parseDiscount(perk?.tag ?? '').value,
  image: [],
  link: perk?.link ?? '',
  logo: [],
  name: perk?.name ?? '',
  perkType: perk?.url ? PerkType.url : PerkType.link,
  url: perk?.url ?? '',
})
